import { useState, useMemo, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { DataGrid, KeenIcon } from '@/components';
import { Filter, X } from 'lucide-react';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useNavigate, useParams } from 'react-router-dom';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { enqueueSnackbar } from 'notistack';
import {
  MemberRosterEntry,
  MemberRosterTableData,
  MemberStatus,
  POSITIONS_OF_TRUST,
  MemberRosterQueryParams,
} from '@/types/members-roster.types';
import {
  useGetAllMemberRostersQuery,
  useDeleteMemberRosterMutation,
  useUpdateMemberRosterStatusMutation,
} from '@/services/ClubMembersRosterService';
import { MembershipRequestStatus } from '@/constant';
import { formatDateWithOptions, formatDisplayDate } from '@/utils/Date';
import {
  useGetAllMembershipRequestsQuery,
  useReviewMembershipRequestMutation,
} from '@/services/ClubService';

interface PendingRequestUser {
  id: string;
  name: string | null;
  email: string | null;
  mobile: string | null;
}

interface PendingMembershipRequest {
  id: string;
  message: string;
  status: number;
  user: PendingRequestUser;
  created_at: string;
}

interface PendingClubRequestRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: number;
  submitted_at: string;
}

const PendingClubRequestsTab = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const { id: clubId } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    requestId: string | null;
    action: 'accept' | 'reject' | null;
    reason?: string;
  }>({ isOpen: false, requestId: null, action: null });

  const queryParams = {
    page: pagination.pageIndex + 1,
    pageLimit: pagination.pageSize,
    search: debouncedValue,
    club_id: clubId || undefined,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAllMembershipRequestsQuery(queryParams, {
      skip: !clubId,
    });

  const [reviewMembershipRequest, { isLoading: isReviewing }] =
    useReviewMembershipRequestMutation();

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    refetch();
  }, [debouncedValue, refetch]);

  const transformData = (
    requests: PendingMembershipRequest[]
  ): PendingClubRequestRow[] => {
    return requests.map((request) => ({
      id: request.id,
      name: request.user?.name || 'Unknown',
      email: request.user?.email || 'No email',
      phone: request.user?.mobile || 'No phone',
      message: request.message,
      status: request.status,
      submitted_at: request.created_at,
    }));
  };

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    let result = transformData(data.data as PendingMembershipRequest[]);
    if (debouncedValue) {
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(debouncedValue.toLowerCase()) ||
          item.email.toLowerCase().includes(debouncedValue.toLowerCase())
      );
    }
    return result;
  }, [data, debouncedValue]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const handleActionClick = useCallback(
    (requestId: string, action: 'accept' | 'reject') => {
      setConfirmActionState({ isOpen: true, requestId, action });
    },
    []
  );

  const handleConfirmAction = async () => {
    if (confirmActionState.requestId && confirmActionState.action) {
      const reviewData = {
        status:
          confirmActionState.action === 'accept'
            ? MembershipRequestStatus.APPROVED.toString()
            : MembershipRequestStatus.REJECTED.toString(),
        reason:
          confirmActionState.action === 'reject'
            ? confirmActionState.reason
            : undefined,
      };

      try {
        await reviewMembershipRequest({
          id: confirmActionState.requestId,
          review: reviewData,
        }).unwrap();
        refetch();
        setConfirmActionState({
          isOpen: false,
          requestId: null,
          action: null,
          reason: undefined,
        });
      } catch (error) {
        console.error('Failed to review membership request:', error);
      }
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmActionState((prev) => ({ ...prev, reason: e.target.value }));
  };

  const renderConfirmationMessage = () => {
    if (confirmActionState.action === 'reject') {
      return (
        <>
          Are you sure you want to reject this membership request?
          <input
            type="text"
            name="membership_reject_reason"
            aria-label="Rejection reason"
            placeholder="Reason (optional)"
            value={confirmActionState.reason || ''}
            onChange={handleReasonChange}
            className="mt-2 w-full p-2 border rounded bg-gray-200 placeholder:text-gray-400"
            disabled={isReviewing}
            autoComplete="off"
          />
        </>
      );
    }
    if (confirmActionState.action === 'accept') {
      return 'Are you sure you want to accept this membership request?';
    }
    return 'Are you sure you want to review this membership request?';
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: PendingClubRequestRow) => row.email,
        id: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }: { row: { original: PendingClubRequestRow } }) => (
          <span className="text-gray-800 font-normal break-all md:break-normal">
            {row.original.email}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: PendingClubRequestRow) => row.phone,
        id: 'phone',
        header: 'Phone',
        enableSorting: false,
        cell: ({ row }: { row: { original: PendingClubRequestRow } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.phone}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: PendingClubRequestRow) => row.message,
        id: 'message',
        header: 'Message',
        enableSorting: false,
        cell: ({ row }: { row: { original: PendingClubRequestRow } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.message || '-'}
          </span>
        ),
        meta: { className: 'min-w-[250px]' },
      },
      {
        accessor: (row: PendingClubRequestRow) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: PendingClubRequestRow } }) => {
          const statusLabel =
            row.original.status === MembershipRequestStatus.PENDING
              ? 'Pending'
              : row.original.status === MembershipRequestStatus.APPROVED
                ? 'Accepted'
                : row.original.status === MembershipRequestStatus.REJECTED
                  ? 'Rejected'
                  : 'Pending';

          const colorClass =
            statusLabel === 'Accepted'
              ? 'text-green-700 dark:text-green-600 contrast:text-green-600'
              : statusLabel === 'Rejected'
                ? 'text-red-600'
                : 'text-yellow-700 dark:text-yellow-600 contrast:text-yellow-600';

          return (
            <span className={`text-sm font-medium ${colorClass}`}>
              {statusLabel}
            </span>
          );
        },
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: PendingClubRequestRow) => row.submitted_at,
        id: 'submitted_at',
        header: 'Submitted At',
        enableSorting: false,
        cell: ({ row }: { row: { original: PendingClubRequestRow } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.submitted_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: PendingClubRequestRow } }) => (
          <div className="flex items-center gap-2">
            {row.original.status === MembershipRequestStatus.PENDING &&
              hasPermission('review_membership_request') && (
                <>
                  <button
                    type="button"
                    onClick={() => handleActionClick(row.original.id, 'accept')}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-green-500"
                    title="Accept"
                    aria-label={`Accept ${row.original.name}'s application`}
                    disabled={isReviewing}
                  >
                    <KeenIcon
                      type="filled"
                      icon="check"
                      className="text-green-500"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActionClick(row.original.id, 'reject')}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Reject"
                    aria-label={`Reject ${row.original.name}'s application`}
                    disabled={isReviewing}
                  >
                    <KeenIcon
                      type="filled"
                      icon="cross"
                      className="text-red-500"
                    />
                  </button>
                </>
              )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleActionClick, hasPermission, isReviewing]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-pending-requests"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-pending-requests"
                placeholder="Search requests"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search pending club requests by name or email"
                disabled={isLoading || isFetching || isReviewing}
              />
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {searchTerm && (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
              disabled={isLoading || isFetching || isReviewing}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || isReviewing ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching || isReviewing}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              totalItems: data?.totalItems || totalItems,
              totalPages: data?.totalPages || totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'email', desc: false }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!searchTerm}
            aria-label="Pending club membership requests table"
          />
        )}
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              requestId: null,
              action: null,
              reason: undefined,
            })
          }
          onConfirm={handleConfirmAction}
          message={renderConfirmationMessage()}
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="pending-action-confirmation-title"
          aria-describedby="pending-action-confirmation-description"
        />
      </div>
    </div>
  );
};

export const MembersRosterListing = () => {
  const navigate = useNavigate();
  const { id: clubId } = useParams<{ id: string }>();
  const intl = useIntl();
  const { hasPermission } = usePermissions();

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  // Filters
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [anniversaryDate, setAnniversaryDate] = useState<string>('');

  // Prepare query params
  const queryParams: MemberRosterQueryParams = useMemo(
    () => ({
      club_id: clubId,
      page: pagination.pageIndex + 1, // API usually 1-indexed, usePagination 0-indexed
      limit: pagination.pageSize,
      search: debouncedValue,
      position_of_trust: positionFilter !== 'all' ? positionFilter : undefined,
      created_at: anniversaryDate || undefined,
      status:
        statusFilter !== 'all'
          ? statusFilter === 'active'
            ? MemberStatus.ACTIVE
            : MemberStatus.SUSPENDED
          : undefined,
    }),
    [
      clubId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedValue,
      positionFilter,
      anniversaryDate,
      statusFilter,
    ]
  );

  // API Query
  const {
    data: rosterData,
    isLoading: isRosterLoading,
    isFetching: isRosterFetching,
  } = useGetAllMemberRostersQuery(queryParams, {
    skip: !clubId,
    refetchOnMountOrArgChange: true,
  });

  const [deleteMemberRoster] = useDeleteMemberRosterMutation();
  const [updateMemberRosterStatus] = useUpdateMemberRosterStatusMutation();

  // UI states
  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    isOpen: boolean;
    rosterId: string | null;
  }>({ isOpen: false, rosterId: null });

  // View state
  const [viewType, setViewType] = useState<'table' | 'grid'>('grid');
  const [activeTab, setActiveTab] = useState<'roster' | 'requests'>('roster');

  // Transform data for display (do not change server-side paging; just map fields)
  const transformData = (
    entries: MemberRosterEntry[]
  ): MemberRosterTableData[] => {
    return entries.map((entry) => {
      // Determine member name
      let memberName = entry.name;
      if (!memberName && entry.user) {
        memberName =
          entry.user.name ||
          `${entry.user.first_name || ''} ${entry.user.last_name || ''}`.trim();
      }
      if (!memberName) {
        memberName = entry.billing_name || 'Unknown';
      }

      // Determine member email
      let memberEmail = entry.email;
      if (!memberEmail && entry.user) {
        memberEmail = entry.user.email;
      }
      if (!memberEmail) {
        memberEmail = entry.billing_email || 'No email';
      }

      const status =
        entry.member_status === MemberStatus.ACTIVE ? 'Active' : 'Suspended';

      return {
        id: entry.id,
        member_name: memberName,
        member_email: memberEmail || 'No email',
        membership_number: entry.federation_membership_number,
        svp_id: entry.svp_membership_id,
        position_of_trust: entry.position_of_trust
          ? POSITIONS_OF_TRUST.find((p) => p.id === entry.position_of_trust)
              ?.label || entry.position_of_trust
          : '—',
        membership_category: entry.membership_category || 'Uncategorized',
        status,
        status_id: entry.member_status,
        anniversary_date: entry.created_at,
        boats_count: entry.boats?.length || 0,
      };
    });
  };

  // Data to display
  const displayData = rosterData?.data ? transformData(rosterData.data) : [];
  const isLoading = isRosterLoading || isRosterFetching;

  // Update pagination totals based on API response (mirror ServiceListings pattern)
  useEffect(() => {
    if (rosterData) {
      const totalCount =
        rosterData.totalItems ||
        rosterData.total_items ||
        rosterData.total ||
        rosterData.data?.length ||
        0;
      const pagesCount = rosterData.totalPages || rosterData.total_pages || 1;

      handleTotalItems(totalCount);
      handleTotalPages(pagesCount);
    }
  }, [rosterData, handleTotalItems, handleTotalPages]);

  const resetFilters = () => {
    resetSearch();
    setPositionFilter('all');
    setStatusFilter('all');
    setAnniversaryDate('');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const hasActiveFilters =
    !!searchTerm ||
    positionFilter !== 'all' ||
    statusFilter !== 'all' ||
    !!anniversaryDate;

  // Render Category Badge
  const renderCategoryBadge = (categoryStr: string | null) => {
    if (!categoryStr) {
      return (
        <span className="px-2.5 py-1 rounded-lg border flex items-center gap-1.5 text-xs font-semibold capitalize tracking-tight bg-gray-100 dark:bg-coal-600 text-gray-700 dark:text-gray-light-700 border-gray-200 dark:border-coal-100">
          <KeenIcon icon="profile-user" className="text-xs" />
          Uncategorized
        </span>
      );
    }
    const category = categoryStr.toLowerCase();
    let colorClass =
      'bg-gray-100 dark:bg-coal-600 text-gray-700 dark:text-gray-light-700 border-gray-200 dark:border-coal-100';
    let icon = 'profile-user';

    if (category.includes('permanent')) {
      colorClass =
        'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-800/40';
      icon = 'star';
    } else if (category.includes('honorary')) {
      colorClass =
        'bg-purple-50 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200 border-purple-200 dark:border-purple-800/40';
      icon = 'award';
    } else if (category.includes('junior')) {
      colorClass =
        'bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-200 border-teal-200 dark:border-teal-800/40';
      icon = 'user';
    } else if (category.includes('invited')) {
      colorClass =
        'bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200 border-amber-200 dark:border-amber-800/40';
      icon = 'send';
    }

    return (
      <span
        className={`px-2.5 py-1 rounded-lg border flex items-center gap-1.5 text-xs font-semibold capitalize tracking-tight ${colorClass}`}
      >
        <KeenIcon icon={icon} className="text-xs" />
        {categoryStr.replace(/_/g, ' ')}
      </span>
    );
  };

  // Actions
  const handleAddClick = () => {
    navigate(`/club-management/${clubId}/roster/add`);
  };

  const handleEditClick = useCallback(
    (rosterId: string) => {
      navigate(`/club-management/${clubId}/roster/edit/${rosterId}`);
    },
    [navigate, clubId]
  );

  const handleViewClick = useCallback(
    (rosterId: string) => {
      navigate(`/club-management/${clubId}/roster/view/${rosterId}`);
    },
    [navigate, clubId]
  );

  const handleBoatsClick = useCallback(
    (rosterId: string) => {
      navigate(`/club-management/${clubId}/roster/${rosterId}/boats`);
    },
    [navigate, clubId]
  );

  const handleDeleteClick = useCallback((rosterId: string) => {
    setDeleteConfirmState({ isOpen: true, rosterId });
  }, []);

  const handleConfirmDelete = async () => {
    if (deleteConfirmState.rosterId) {
      try {
        await deleteMemberRoster(deleteConfirmState.rosterId).unwrap();
        enqueueSnackbar('Member roster entry deleted successfully', {
          variant: 'success',
        });
        setDeleteConfirmState({ isOpen: false, rosterId: null });
      } catch (error) {
        console.error('Failed to delete roster entry:', error);
        enqueueSnackbar('Failed to delete roster entry', { variant: 'error' });
      }
    }
  };

  const handleStatusChange = async (
    rosterId: string,
    newStatus: MemberStatus
  ) => {
    try {
      await updateMemberRosterStatus({
        id: rosterId,
        status: newStatus,
      }).unwrap();
      enqueueSnackbar('Status updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
  };

  // Render Status Badge
  const renderStatusBadge = (
    status: string,
    statusId: MemberStatus,
    rosterId: string
  ) => {
    let colorClass =
      'bg-gray-100 dark:bg-coal-600 contrast:bg-coal-600 text-gray-700 dark:text-gray-light-700 contrast:text-gray-light-700 border-gray-200 dark:border-coal-100 contrast:border-coal-100';

    if (statusId === MemberStatus.ACTIVE) {
      colorClass =
        'bg-emerald-100 dark:bg-emerald-900/20 contrast:bg-emerald-900/20 text-emerald-800 dark:text-emerald-400 contrast:text-emerald-400 border-emerald-200 dark:border-emerald-800/40 contrast:border-emerald-800/40';
    } else if (statusId === MemberStatus.SUSPENDED) {
      colorClass =
        'bg-red-100 dark:bg-red-900/20 contrast:bg-red-900/20 text-red-800 dark:text-red-400 contrast:text-red-400 border-red-200 dark:border-red-800/40 contrast:border-red-800/40';
    }

    return (
      <div className="relative inline-block">
        <select
          aria-label="Change member status"
          value={statusId}
          onChange={(e) =>
            handleStatusChange(rosterId, Number(e.target.value) as MemberStatus)
          }
          className={`appearance-none px-3 py-1.5 rounded-full border text-xs font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 cursor-pointer text-center pr-8 ${colorClass} min-w-[110px]`}
        >
          <option
            value={MemberStatus.ACTIVE}
            hidden={statusId === MemberStatus.ACTIVE}
            className="bg-white dark:bg-coal-600 contrast:bg-coal-600 text-emerald-600 dark:text-emerald-400 contrast:text-emerald-400"
          >
            {intl.formatMessage({
              id: 'MEMBERS_ROSTER.STATUS.ACTIVE',
              defaultMessage: 'Active',
            })}
          </option>
          <option
            value={MemberStatus.SUSPENDED}
            hidden={statusId === MemberStatus.SUSPENDED}
            className="bg-white dark:bg-coal-600 contrast:bg-coal-600 text-red-600 dark:text-red-400 contrast:text-red-400"
          >
            {intl.formatMessage({
              id: 'MEMBERS_ROSTER.STATUS.SUSPENDED',
              defaultMessage: 'Suspended',
            })}
          </option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
          <KeenIcon icon="down" className="text-[10px] opacity-70" />
        </div>
      </div>
    );
  };

  const renderStatusBadgeOnly = (status: string, statusId: MemberStatus) => {
    let colorClass =
      'bg-gray-100 dark:bg-coal-600 text-gray-700 dark:text-gray-light-700 border-gray-200 dark:border-coal-100';

    if (statusId === MemberStatus.ACTIVE) {
      colorClass =
        'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40';
    } else if (statusId === MemberStatus.SUSPENDED) {
      colorClass =
        'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 border-red-200 dark:border-red-800/40';
    }

    return (
      <span
        className={`px-3 py-1.5 rounded-full border text-xs font-bold ${colorClass} min-w-[110px] inline-block text-center`}
      >
        {status}
      </span>
    );
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessor: (row: MemberRosterTableData) => row.member_name,
        id: 'member_name',
        header: 'Member Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => (
          <div className="flex flex-col min-w-0">
            {hasPermission('club_member_roster_management_read') ? (
              <button
                onClick={() => handleViewClick(row.original.id)}
                className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-semibold hover:text-blue-600 dark:hover:text-blue-400 contrast:hover:text-blue-400 truncate text-sm text-left"
              >
                {row.original.member_name || 'Unknown Member'}
                {row.original.member_name || 'Unknown Member'}
              </button>
            ) : (
              <span className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-semibold truncate text-sm">
                {row.original.member_name || 'Unknown Member'}
              </span>
            )}
            <span className="text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 text-xs flex items-center gap-1 font-medium truncate">
              <KeenIcon
                icon="sms"
                className="text-xs dark:text-gray-light-400 contrast:text-gray-light-400"
              />
              {row.original.member_email || 'No email'}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.membership_number,
        id: 'membership_number',
        header: 'Membership #',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-medium">
              Federation
            </span>
            <span className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-mono text-sm font-semibold">
              {row.original.membership_number}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[140px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.svp_id,
        id: 'svp_id',
        header: 'SVP ID',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-medium">
              National
            </span>
            <span className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-mono text-sm font-semibold">
              {row.original.svp_id}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.membership_category,
        id: 'category',
        header: 'Category',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) =>
          renderCategoryBadge(row.original.membership_category),
        meta: { className: 'min-w-[160px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.position_of_trust,
        id: 'position',
        header: 'Position',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => {
          const isNone = row.original.position_of_trust === '—';
          return (
            <div className="flex flex-col">
              {isNone ? (
                <span className="text-gray-400 dark:text-gray-light-400 contrast:text-gray-light-400 italic text-sm">
                  —
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  <span className="text-blue-900 dark:text-blue-400 contrast:text-blue-400 font-medium text-sm">
                    {row.original.position_of_trust}
                  </span>
                </div>
              )}
            </div>
          );
        },
        meta: { className: 'min-w-[160px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) =>
          hasPermission('club_member_roster_management_update')
            ? renderStatusBadge(
                row.original.status,
                row.original.status_id,
                row.original.id
              )
            : renderStatusBadgeOnly(
                row.original.status,
                row.original.status_id
              ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.boats_count,
        id: 'boats',
        header: 'Boats',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => (
          <button
            onClick={() => handleBoatsClick(row.original.id)}
            className="flex items-center hover:scale-105 cursor-pointer focus:outline-none"
          >
            {row.original.boats_count > 0 ? (
              <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 contrast:bg-blue-900/20 px-2 py-1 rounded-md border border-blue-100 dark:border-blue-800/20 contrast:border-blue-800/20">
                <KeenIcon
                  icon="ship"
                  className="text-blue-600 dark:text-blue-400 contrast:text-blue-400 text-sm"
                />
                <span className="text-blue-800 dark:text-blue-200 contrast:text-blue-200 font-semibold text-sm">
                  {row.original.boats_count}
                </span>
              </div>
            ) : (
              <span className="text-gray-300 dark:text-coal-100 contrast:text-coal-100 hover:text-blue-400 dark:hover:text-blue-400 contrast:hover:text-blue-400">
                —
              </span>
            )}
          </button>
        ),
        meta: { className: 'min-w-[80px]' },
      },
      {
        accessor: (row: MemberRosterTableData) => row.anniversary_date,
        id: 'anniversary',
        header: 'Anniversary',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => (
          <div className="flex flex-col">
            <span className="text-gray-700 dark:text-gray-light-100 contrast:text-gray-light-100 font-semibold text-sm">
              {row.original.anniversary_date
                ? formatDateWithOptions(
                    new Date(row.original.anniversary_date),
                    {
                      month: 'short',
                      day: 'numeric',
                    },
                    'en-US'
                  )
                : '—'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500">
              {row.original.anniversary_date
                ? `Since ${new Date(row.original.anniversary_date).getFullYear()}`
                : 'No date'}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: MemberRosterTableData } }) => (
          <div className="flex items-center gap-1">
            {hasPermission('club_member_roster_management_read') && (
              <button
                type="button"
                onClick={() => handleViewClick(row.original.id)}
                className="btn btn-sm btn-icon btn-light dark:btn-light contrast:btn-light btn-outline dark:btn-outline contrast:btn-outline hover:bg-blue-50 dark:hover:bg-blue-900/20 contrast:hover:bg-blue-900/20 border border-gray-200 dark:border-coal-100 contrast:border-coal-100"
                title="View Details"
                aria-label="View Details"
              >
                <KeenIcon
                  type="filled"
                  icon="eye"
                  className="text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500"
                />
              </button>
            )}
            {hasPermission('club_member_roster_management_update') && (
              <button
                type="button"
                onClick={() => handleEditClick(row.original.id)}
                className="btn btn-sm btn-icon btn-light dark:btn-light contrast:btn-light btn-outline dark:btn-outline contrast:btn-outline hover:bg-indigo-50 dark:hover:bg-indigo-900/20 contrast:hover:bg-indigo-900/20 border border-gray-200 dark:border-coal-100 contrast:border-coal-100"
                title="Edit"
                aria-label="Edit"
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-blue-600 dark:text-blue-400 contrast:text-blue-400"
                />
              </button>
            )}
            {hasPermission('club_member_roster_management_delete') && (
              <button
                type="button"
                onClick={() => handleDeleteClick(row.original.id)}
                className="btn btn-sm btn-icon btn-light dark:btn-light contrast:btn-light btn-outline dark:btn-outline contrast:btn-outline hover:bg-rose-50 dark:hover:bg-rose-900/10 contrast:hover:bg-rose-900/10 border border-gray-200 dark:border-coal-100 contrast:border-coal-100"
                title="Delete"
                aria-label="Delete"
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-red-500 dark:text-red-400 contrast:text-red-400"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[150px]' },
      },
    ],
    [hasPermission, handleViewClick, handleEditClick, handleDeleteClick]
  );

  return (
    <div className="card card-grid w-full overflow-x-hidden roster-container">
      {/* Roster header with distinctive styling */}
      <div className="card-header bg-white dark:bg-coal-300 contrast:bg-neutral-900 border-b border-gray-200 dark:border-coal-100 contrast:border-coal-100 py-6 px-8">
        <div className="flex flex-col gap-6 w-full">
          {/* Top Row: Title and Main Action */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 tracking-tight leading-none">
                  Members Roster
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {activeTab === 'roster' && (
                <>
                  {/* View Switcher */}
                  <div className="flex items-center bg-gray-100 dark:bg-coal-200 contrast:bg-coal-200 p-1 rounded-lg border border-gray-200 dark:border-coal-100 contrast:border-coal-100">
                    <button
                      className={`btn btn-icon btn-sm rounded-md ${viewType === 'table' ? 'bg-white dark:bg-coal-400 contrast:bg-coal-400 text-blue-600 shadow-sm' : 'text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 hover:text-gray-900 dark:hover:text-white contrast:hover:text-white'}`}
                      onClick={() => setViewType('table')}
                      title="Table View"
                    >
                      <KeenIcon icon="row-horizontal" />
                    </button>
                    <button
                      className={`btn btn-icon btn-sm rounded-md ${viewType === 'grid' ? 'bg-white dark:bg-coal-400 contrast:bg-coal-400 text-blue-600 shadow-sm' : 'text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 hover:text-gray-900 dark:hover:text-white contrast:hover:text-white'}`}
                      onClick={() => setViewType('grid')}
                      title="Grid View"
                    >
                      <KeenIcon icon="category" />
                    </button>
                  </div>

                  {hasPermission('club_member_roster_management_create') && (
                    <button
                      className="btn btn-sm btn-light"
                      onClick={handleAddClick}
                      disabled={isLoading}
                    >
                      Add Member
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          {hasPermission('read_membership_requests') && (
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={() => setActiveTab('roster')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
                  activeTab === 'roster'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white dark:bg-coal-300 contrast:bg-coal-300 text-gray-600 dark:text-gray-light-600 contrast:text-gray-light-600 border-gray-200 dark:border-coal-100 contrast:border-coal-100'
                }`}
              >
                Members
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('requests')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
                  activeTab === 'requests'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white dark:bg-coal-300 contrast:bg-coal-300 text-gray-600 dark:text-gray-light-600 contrast:text-gray-light-600 border-gray-200 dark:border-coal-100 contrast:border-coal-100'
                }`}
              >
                Pending Requests
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter Section - Consistent Design */}
      {activeTab === 'roster' && (
        <div className="px-6 py-6 bg-gray-50/50 dark:bg-coal-600/50 contrast:bg-coal-600/50">
          <div className="bg-white dark:bg-coal-300 contrast:bg-coal-300 rounded-2xl shadow-lg border border-gray-100 dark:border-coal-100 contrast:border-coal-100 overflow-hidden mb-6">
            <div className="bg-blue-100 dark:bg-blue-900/20 contrast:bg-blue-900/20 px-6 py-4 border-b border-gray-100 dark:border-coal-100 contrast:border-coal-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-300 dark:bg-blue-900 contrast:bg-blue-900 rounded-lg">
                  <Filter className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100">
                    Filters & Search
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-light-600 contrast:text-gray-light-600">
                    Refine the member list
                  </p>
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-light-600 contrast:text-gray-light-600 bg-white dark:bg-coal-200 contrast:bg-coal-200 hover:bg-gray-50 dark:hover:bg-coal-100 contrast:hover:bg-coal-100 hover:text-gray-900 dark:hover:text-white contrast:hover:text-white rounded-lg border border-gray-200 dark:border-coal-100 contrast:border-coal-100 shadow-sm flex items-center gap-2"
                >
                  <X className="w-3.5 h-3.5" />
                  Reset All
                </button>
              )}
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div className="col-span-1 md:col-span-2 lg:col-span-1">
                  <label
                    htmlFor="members-roster-search"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-light-700 contrast:text-gray-light-700 mb-1.5 ml-1"
                  >
                    Search
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeenIcon
                        type="filled"
                        icon="magnifier"
                        className="text-gray-400 dark:text-gray-light-500 contrast:text-gray-light-500 group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400 contrast:group-focus-within:text-blue-400"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Search name, ID or email..."
                      value={searchTerm}
                      id="members-roster-search"
                      name="search"
                      aria-label="Search members"
                      autoComplete="off"
                      onChange={handleSearch}
                      className="block w-full rounded-md border border-gray-300 dark:border-coal-100 contrast:border-coal-100 pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/20 contrast:focus:ring-blue-900/20 focus:border-blue-500 shadow-sm bg-gray-50/30 dark:bg-coal-600/30 contrast:bg-coal-600/30 focus:bg-white dark:focus:bg-coal-600 contrast:focus:bg-coal-600 placeholder:text-gray-400 dark:placeholder:text-gray-light-400 contrast:placeholder:text-gray-light-400 dark:text-white contrast:text-white"
                    />
                  </div>
                </div>

                {/* Position Filter */}
                <div>
                  <label
                    htmlFor="members-roster-position"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-light-700 contrast:text-gray-light-700 mb-1.5 ml-1"
                  >
                    Position
                  </label>
                  <select
                    id="members-roster-position"
                    name="position"
                    aria-label="Filter by position of trust"
                    value={positionFilter}
                    onChange={(e) => {
                      setPositionFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  >
                    <option value="all">All Positions</option>
                    {POSITIONS_OF_TRUST.map((pos) => (
                      <option key={pos.id} value={pos.id}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label
                    htmlFor="members-roster-status"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-light-700 contrast:text-gray-light-700 mb-1.5 ml-1"
                  >
                    Status
                  </label>
                  <select
                    id="members-roster-status"
                    name="status"
                    aria-label="Filter by member status"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Anniversary Filter */}
                <div>
                  <label
                    htmlFor="members-roster-membership-date"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-light-700 contrast:text-gray-light-700 mb-1.5 ml-1"
                  >
                    Memb. Date
                  </label>
                  <input
                    id="members-roster-membership-date"
                    type="date"
                    name="membership_date"
                    autoComplete="off"
                    value={anniversaryDate}
                    onChange={(e) => {
                      setAnniversaryDate(e.target.value);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    aria-label="Member date"
                    className="block input w-full rounded-md border border-gray-300 dark:border-coal-100 contrast:border-coal-100 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/20 contrast:focus:ring-blue-900/20 shadow-sm bg-gray-50/30 dark:bg-coal-600/30 contrast:bg-coal-600/30 focus:bg-white dark:focus:bg-coal-600 contrast:focus:bg-coal-600 text-gray-700 dark:text-white contrast:text-white"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Content Area */}
      <div className="card-body p-6 bg-gray-50/50 dark:bg-coal-600/50 contrast:bg-coal-600/50">
        {activeTab === 'roster' &&
          (isLoading ? (
            <div className="text-center py-12">
              <KeenIcon
                type="filled"
                icon="spinner"
                className="text-blue-500 text-4xl animate-spin"
              />
              <p className="mt-4 text-gray-600 dark:text-gray-light-500 contrast:text-gray-light-500 font-bold tracking-widest uppercase text-xs">
                Accessing Roster...
              </p>
            </div>
          ) : viewType === 'table' ? (
            <DataGrid
              columns={columns}
              data={displayData}
              rowSelect={false}
              loading={isLoading}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems: totalItems,
                totalPages: totalPages,
                setPagination,
                info: '{from} - {to} of {count}',
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'member_name', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={hasActiveFilters}
            />
          ) : (
            <div className="flex flex-col gap-6">
              {displayData.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {displayData.map((member) => (
                      <div
                        key={member.id}
                        className="member-card bg-white dark:bg-coal-300 contrast:bg-coal-300 rounded-2xl border border-gray-100 dark:border-coal-100 contrast:border-coal-100 shadow-md overflow-hidden flex flex-col group"
                      >
                        {/* Card Top / Header */}
                        <div className="p-5 flex flex-col gap-3">
                          <div className="flex justify-between items-start">
                            <div className="flex flex-col flex-1 min-w-0">
                              <h3 className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-bold text-lg leading-tight truncate">
                                {member.member_name || 'Unknown Member'}
                              </h3>
                              <p className="text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 text-xs font-medium truncate flex items-center gap-1 mt-1">
                                <KeenIcon icon="sms" className="text-xs" />
                                {member.member_email || 'No email'}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              {hasPermission(
                                'club_member_roster_management_update'
                              )
                                ? renderStatusBadge(
                                    member.status,
                                    member.status_id,
                                    member.id
                                  )
                                : renderStatusBadgeOnly(
                                    member.status,
                                    member.status_id
                                  )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {renderCategoryBadge(member.membership_category)}
                          </div>
                        </div>

                        {/* Card Middle / Details */}
                        <div className="px-5 py-4 bg-gray-50 dark:bg-coal-200 contrast:bg-coal-200 border-y border-gray-100 dark:border-coal-100 contrast:border-coal-100 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-semibold uppercase tracking-wider">
                              Federation ID
                            </span>
                            <span className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-mono text-sm font-semibold">
                              {member.membership_number || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-semibold uppercase tracking-wider">
                              SVP ID
                            </span>
                            <span className="text-gray-900 dark:text-gray-light-100 contrast:text-gray-light-100 font-mono text-sm font-semibold">
                              {member.svp_id || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-1 border-t border-gray-200/50 dark:border-coal-100/50 contrast:border-coal-100/50">
                            <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-semibold uppercase tracking-wider">
                              Position
                            </span>
                            <span
                              className={`text-sm font-semibold ${member.position_of_trust === '—' ? 'text-gray-400 dark:text-gray-light-400 contrast:text-gray-800 italic' : 'text-blue-800 dark:text-blue-400 contrast:text-blue-400'}`}
                            >
                              {member.position_of_trust}
                            </span>
                          </div>
                        </div>

                        {/* Card Footer / Stats & Actions */}
                        <div className="p-5 mt-auto bg-white dark:bg-coal-300 contrast:bg-coal-300 flex flex-col gap-4">
                          <div className="flex justify-between items-center">
                            <button
                              onClick={() => handleBoatsClick(member.id)}
                              className="flex items-center gap-2 bg-blue-50/50 dark:bg-blue-900/10 contrast:bg-blue-900/10 px-3 py-1.5 rounded-xl border border-blue-100/50 dark:border-blue-800/20 contrast:border-blue-800/20 hover:bg-blue-100 dark:hover:bg-blue-900/20 contrast:hover:bg-blue-900/20 hover:border-blue-200 dark:hover:border-blue-800/40 contrast:hover:border-blue-800/40 cursor-pointer text-left"
                            >
                              <KeenIcon
                                icon="ship"
                                className="text-blue-600 dark:text-blue-400 contrast:text-blue-400 text-lg"
                              />
                              <div className="flex flex-col">
                                <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-semibold leading-none mb-0.5">
                                  Boats
                                </span>
                                <span className="text-blue-900 dark:text-blue-300 contrast:text-blue-300 font-bold text-sm leading-none">
                                  {member.boats_count}
                                </span>
                              </div>
                            </button>
                          </div>

                          <div className="flex items-center gap-2 pt-2">
                            {hasPermission(
                              'club_member_roster_management_read'
                            ) && (
                              <button
                                type="button"
                                onClick={() => handleViewClick(member.id)}
                                className="btn btn-sm btn-light flex-1 flex items-center justify-center gap-2 rounded-xl text-xs font-bold hover:bg-blue-50 dark:hover:bg-coal-200 contrast:hover:bg-coal-200 hover:text-blue-600 dark:hover:text-blue-400 contrast:hover:text-blue-400 border border-gray-200 dark:border-coal-100 contrast:border-coal-100"
                              >
                                <KeenIcon icon="eye" className="text-sm" />
                                View
                              </button>
                            )}
                            {hasPermission(
                              'club_member_roster_management_update'
                            ) && (
                              <button
                                type="button"
                                onClick={() => handleEditClick(member.id)}
                                className="btn btn-sm btn-primary flex-1 flex items-center justify-center gap-2 rounded-xl text-xs font-bold"
                              >
                                <KeenIcon icon="pencil" className="text-sm" />
                                Edit
                              </button>
                            )}
                            {hasPermission(
                              'club_member_roster_management_delete'
                            ) && (
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(member.id)}
                                aria-label="Delete member"
                                title="Delete member"
                                className="btn btn-sm btn-icon btn-light dark:btn-light contrast:btn-light rounded-xl h-[38px] w-[38px] border border-gray-200 dark:border-coal-100 contrast:border-coal-100 hover:text-red-600 dark:hover:text-red-400 contrast:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 contrast:hover:bg-red-900/10"
                              >
                                <KeenIcon icon="trash" className="text-sm" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination for Grid view - Identical to Table view */}
                  <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-3 text-gray-600 dark:text-gray-light-600 contrast:text-gray-light-600 text-2sm font-medium mt-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-bold">
                        Show
                      </span>
                      <select
                        aria-label="Select page size"
                        className="select select-sm w-16 bg-white dark:bg-coal-600 contrast:bg-coal-600 dark:text-white contrast:text-white border-gray-300 dark:border-coal-100 contrast:border-coal-100"
                        value={pagination.pageSize}
                        onChange={(e) =>
                          setPagination((prev) => ({
                            ...prev,
                            pageSize: Number(e.target.value),
                            pageIndex: 0,
                          }))
                        }
                      >
                        {[5, 10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 font-bold">
                        per page
                      </span>
                    </div>

                    <div className="flex items-center gap-4 order-1 md:order-2">
                      <span className="whitespace-nowrap">
                        {totalItems > 0 ? (
                          <>
                            {pagination.pageIndex * pagination.pageSize + 1} -{' '}
                            {Math.min(
                              (pagination.pageIndex + 1) * pagination.pageSize,
                              totalItems
                            )}{' '}
                            of{' '}
                            <span className="text-gray-900 dark:text-white contrast:text-white font-bold">
                              {totalItems}
                            </span>
                          </>
                        ) : (
                          '0 - 0 of 0'
                        )}
                      </span>
                      <div className="pagination">
                        {/* Previous */}
                        <button
                          className="btn"
                          onClick={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.max(0, p.pageIndex - 1),
                            }))
                          }
                          disabled={pagination.pageIndex === 0}
                          aria-label="previous page"
                        >
                          <KeenIcon
                            icon="black-left"
                            className="rtl:transform rtl:rotate-180"
                          />
                        </button>

                        {/* Page buttons with grouping and ellipses */}
                        {(() => {
                          const pageCount = totalPages;
                          const moreLimit = 5;
                          const pageIndex = pagination.pageIndex;
                          const start =
                            Math.floor(pageIndex / moreLimit) * moreLimit;
                          const end = Math.min(start + moreLimit, pageCount);
                          return (
                            <div className="flex items-center">
                              {start > 0 && (
                                <button
                                  className="btn"
                                  onClick={() =>
                                    setPagination((p) => ({
                                      ...p,
                                      pageIndex: Math.max(0, start - 1),
                                    }))
                                  }
                                >
                                  ...
                                </button>
                              )}
                              {Array.from({ length: pageCount }, (_, i) => i)
                                .slice(start, end)
                                .map((pageNum) => (
                                  <button
                                    key={pageNum}
                                    className={`btn ${pageIndex === pageNum ? 'active' : ''}`}
                                    onClick={() =>
                                      setPagination((p) => ({
                                        ...p,
                                        pageIndex: pageNum,
                                      }))
                                    }
                                  >
                                    {pageNum + 1}
                                  </button>
                                ))}
                              {end < pageCount && (
                                <button
                                  className="btn"
                                  onClick={() =>
                                    setPagination((p) => ({
                                      ...p,
                                      pageIndex: end,
                                    }))
                                  }
                                >
                                  ...
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        {/* Next */}
                        <button
                          className="btn"
                          onClick={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: Math.min(
                                totalPages - 1,
                                p.pageIndex + 1
                              ),
                            }))
                          }
                          disabled={pagination.pageIndex >= totalPages - 1}
                          aria-label="next page"
                        >
                          <KeenIcon
                            icon="black-right"
                            className="rtl:transform rtl:rotate-180"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white dark:bg-coal-300 contrast:bg-coal-300 rounded-2xl border border-dashed border-gray-300 dark:border-coal-100 contrast:border-coal-100 p-12 flex flex-col items-center justify-center text-center">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white mb-1">
                    No Members Found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-light-500 contrast:text-gray-light-500 text-sm max-w-xs mx-auto">
                    {hasActiveFilters
                      ? "We couldn't find any members matching your current filters. Try adjusting your search or filters."
                      : 'Your member roster is currently empty. Start by adding your first club member.'}
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={resetFilters}
                      className="btn btn-sm btn-light dark:btn-light contrast:btn-light mt-6 rounded-xl font-bold border border-gray-200 dark:border-coal-100 contrast:border-coal-100"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

        {activeTab === 'requests' && <PendingClubRequestsTab />}

        {/* Delete confirmation */}
        <ConfirmationDialog
          open={deleteConfirmState.isOpen}
          onClose={() =>
            setDeleteConfirmState({ isOpen: false, rosterId: null })
          }
          onConfirm={handleConfirmDelete}
          message="Are you sure you want to delete this roster entry? This action cannot be undone."
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
        />
      </div>
    </div>
  );
};
